import React, { useEffect, useMemo, useState } from 'react';
import {
  CompensationRecord,
  deleteRecord,
  fetchCompanyDetail,
  fetchRecords,
  formatDate,
  formatMoney,
  formatSti,
  updateCompany,
} from '../../../services/adminService';
import {
  AdminBadge,
  AdminBreadcrumbsTw,
  AdminCard,
  AdminDevTh,
  AdminEmpty,
  AdminStatCard,
  AdminTableWrap,
  CompanyAvatar,
  btnLink,
  btnPrimary,
  btnSecondary,
  filterSearchWrapClass,
  filtersRowClass,
  inputClass,
  selectClass,
} from './adminUi';
import { AdminModalTw, AdminFieldTw, BreadcrumbItem, ConfirmDialog } from './adminShared';
import { useAdminPermissions } from './adminPermissions';
import { RecordFormPanel, RecordPreviewModal, ResizableRecordPanelShell } from './RecordDrawer';
import QuickAddGrid from './QuickAddGrid';

type Props = {
  companyId: number;
  sectorId?: number;
  quickAdd?: boolean;
  openRecordOnLoad?: boolean;
  onNavigate: (loc: import('./adminShared').AdminLocation) => void;
  onBackToCompanies: () => void;
};

export default function AdminCompanyPage({
  companyId,
  sectorId,
  quickAdd: initialQuickAdd,
  openRecordOnLoad,
  onNavigate,
  onBackToCompanies,
}: Props) {
  const { canManageStructure } = useAdminPermissions();
  const [loading, setLoading] = useState(true);
  const [listRefreshing, setListRefreshing] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [context, setContext] = useState<{
    industry_id: number;
    industry_name: string;
    sector_id: number;
    sector_name: string;
  } | null>(null);
  const [stats, setStats] = useState({
    records_count: 0,
    roles_count: 0,
    last_record_at: null as string | null,
  });
  const [records, setRecords] = useState<CompensationRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('created_at_desc');
  const [quickAdd, setQuickAdd] = useState(Boolean(initialQuickAdd));
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingRecord, setEditingRecord] = useState<CompensationRecord | null>(null);
  const [previewRecord, setPreviewRecord] = useState<CompensationRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CompensationRecord | null>(null);
  const [editCompanyOpen, setEditCompanyOpen] = useState(false);
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const contextLabel = useMemo(() => {
    if (!context) return companyName;
    return `${context.industry_name} → ${context.sector_name} → ${companyName}`;
  }, [context, companyName]);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Админ-панель', onClick: () => onNavigate({ screen: 'overview' }) },
    context
      ? { label: context.industry_name, onClick: () => onNavigate({ screen: 'sectors', industryId: context.industry_id }) }
      : { label: 'Компании', onClick: onBackToCompanies },
    context
      ? {
          label: context.sector_name,
          onClick: () =>
            onNavigate({ screen: 'companies', industryId: context.industry_id, sectorId: context.sector_id }),
        }
      : null,
    { label: companyName || 'Компания' },
  ].filter(Boolean) as BreadcrumbItem[];

  const gradesCount = useMemo(() => {
    const set = new Set(records.map((r) => r.grade_level).filter((g) => g != null));
    return set.size || stats.roles_count;
  }, [records, stats.roles_count]);

  const loadCompany = async () => {
    setLoading(true);
    try {
      const detail = await fetchCompanyDetail(companyId, sectorId);
      if (detail?.company) {
        setCompanyName(detail.company.name);
        setContext(detail.context);
        setStats(detail.stats);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRecords = async () => {
    setListRefreshing(true);
    try {
      const res = await fetchRecords({
        companyId,
        search: search.trim() || undefined,
        sort,
        page,
        limit: pageSize,
      });
      setRecords(res?.data || []);
      setTotal(res?.total || 0);
    } finally {
      setListRefreshing(false);
    }
  };

  useEffect(() => {
    loadCompany();
  }, [companyId, sectorId]);

  useEffect(() => {
    if (!loading) loadRecords();
  }, [companyId, search, sort, page, loading]);

  useEffect(() => {
    if (!openRecordOnLoad) return;
    setFormMode('create');
    setEditingRecord(null);
    setQuickAdd(false);
  }, [openRecordOnLoad, companyId]);

  const refreshAll = async () => {
    await Promise.all([loadCompany(), loadRecords()]);
  };

  const openCreate = () => {
    setFormMode('create');
    setEditingRecord(null);
    setQuickAdd(false);
  };

  const openEdit = (record: CompensationRecord) => {
    setPreviewRecord(null);
    setFormMode('edit');
    setEditingRecord(record);
    setQuickAdd(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteRecord(deleteTarget.id);
    setDeleteTarget(null);
    setFormMode('create');
    setEditingRecord(null);
    await refreshAll();
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);

  if (loading && !companyName) {
    return <AdminEmpty>Загрузка компании…</AdminEmpty>;
  }

  return (
    <div className="dc-admin-company-page">
      <div className="dc-company-page-header">
      <AdminBreadcrumbsTw items={breadcrumbs} />

      <AdminCard className="mb-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex gap-4">
            <CompanyAvatar name={companyName} />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{companyName}</h1>
              {context && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <AdminBadge variant="blue">{context.sector_name}</AdminBadge>
                  <AdminBadge variant="green">{context.industry_name}</AdminBadge>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={btnPrimary} onClick={openCreate}>
              <i className="fas fa-plus text-xs" aria-hidden="true" /> Добавить запись
            </button>
            <button type="button" className={btnSecondary} onClick={() => setQuickAdd((v) => !v)}>
              <i className="fas fa-bolt text-xs text-amber-500" aria-hidden="true" />
              {quickAdd ? 'Скрыть быстрое добавление' : 'Быстрое добавление'}
            </button>
            {canManageStructure && (
              <button
                type="button"
                className={btnSecondary}
                onClick={() => {
                  setEditCompanyName(companyName);
                  setEditError(null);
                  setEditCompanyOpen(true);
                }}
              >
                <i className="fas fa-pen text-xs" aria-hidden="true" /> Редактировать компанию
              </button>
            )}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <AdminStatCard label="Записей сотрудников" value={stats.records_count} />
          <AdminStatCard label="Ролей" value={stats.roles_count} />
          <AdminStatCard label="Грейдов" value={gradesCount} />
          <AdminStatCard label="Последняя запись" value={formatDate(stats.last_record_at)} />
        </div>
      </AdminCard>

      {quickAdd && (
        <div className="mb-5">
          <QuickAddGrid companyId={companyId} onCancel={() => setQuickAdd(false)} onSaved={refreshAll} />
        </div>
      )}
      </div>

      <div className="dc-company-split">
        <div className="dc-company-table-pane min-w-0 flex-1">
          <AdminCard padding={false} className="overflow-hidden dc-card">
            <div className="dc-card-filters">
              <div className={filtersRowClass} style={{ marginBottom: 0 }}>
                <div className={filterSearchWrapClass}>
                  <i className="fas fa-search dc-filter-search-icon" aria-hidden="true" />
                  <input
                    className={`${inputClass} dc-input-with-icon`}
                    placeholder="Поиск по записям"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <select className={selectClass} defaultValue="">
                  <option value="">Роль</option>
                </select>
                <select className={selectClass} defaultValue="">
                  <option value="">Грейд</option>
                </select>
                <select className={selectClass} value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="created_at_desc">Период</option>
                  <option value="added_date_desc">По периоду данных</option>
                </select>
                <select className={selectClass} defaultValue="RUB">
                  <option value="RUB">Валюта</option>
                </select>
                <button
                  type="button"
                  className={btnSecondary}
                  onClick={() => {
                    setSearch('');
                    setSort('created_at_desc');
                    setPage(1);
                  }}
                >
                  <i className="fas fa-redo text-xs" aria-hidden="true" /> Сбросить
                </button>
              </div>
            </div>

            <div className="dc-company-table-scroll">
            <AdminTableWrap refreshing={listRefreshing} className="rounded-none border-0 shadow-none">
              <table className="w-full min-w-[800px] text-sm">
                <thead className="border-b border-slate-100 bg-slate-50/80 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <AdminDevTh label="Роль" dbField="compensation_records.company_role_name" className="px-4 py-3 normal-case" />
                    <AdminDevTh label="Специализация" dbField="roles.name" className="px-4 py-3 normal-case" />
                    <AdminDevTh label="Грейд" dbField="compensation_records.grade_level" className="px-4 py-3 normal-case" />
                    <AdminDevTh label="Период" dbField="compensation_records.added_date" className="px-4 py-3 normal-case" />
                    <AdminDevTh label="FIX" dbField="compensation_record_base.base_gross_salary" className="px-4 py-3 normal-case" />
                    <AdminDevTh label="STI" dbField="compensation_record_sti.sti_target_pct" className="px-4 py-3 normal-case" />
                    <AdminDevTh label="LTIP" dbField="compensation_record_ltip" className="px-4 py-3 normal-case" />
                    <AdminDevTh label="Источник" dbField="compensation_records.source" className="px-4 py-3 normal-case" />
                    <AdminDevTh label="Надёжность" dbField="compensation_records.reliability_score" className="px-4 py-3 normal-case" />
                    <AdminDevTh label="Изм." className="px-4 py-3 w-20 normal-case" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {records.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-12 text-center text-dc-muted">
                        Записей пока нет. Добавьте первую запись справа.
                      </td>
                    </tr>
                  ) : (
                    records.map((record) => (
                      <tr key={record.id} className="hover:bg-slate-50/50">
                        <td className="px-4 py-3">
                          <button type="button" className={btnLink} onClick={() => setPreviewRecord(record)}>
                            {record.company_role_name}
                          </button>
                          {(record.last_name || record.first_name) && (
                            <div className="text-xs text-slate-400">
                              {[record.last_name, record.first_name].filter(Boolean).join(' ')}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{record.role_name || '—'}</td>
                        <td className="px-4 py-3">{record.grade_level ?? '—'}</td>
                        <td className="px-4 py-3">{formatDate(record.added_date)}</td>
                        <td className="px-4 py-3 text-slate-600">{formatMoney(record.fix)}</td>
                        <td className="px-4 py-3 text-slate-600">{formatSti(record.sti)}</td>
                        <td className="px-4 py-3 text-slate-600">{record.ltip ?? (record.ltip_has ? 'да' : '—')}</td>
                        <td className="px-4 py-3 text-slate-600">{record.source}</td>
                        <td className="px-4 py-3 text-slate-600">{(record.reliability_score * 100).toFixed(0)}%</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button type="button" className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-dc-primary" onClick={() => openEdit(record)} aria-label="Изменить">
                              <i className="fas fa-pen text-xs" aria-hidden="true" />
                            </button>
                            {canManageStructure && (
                              <button type="button" className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" onClick={() => setDeleteTarget(record)} aria-label="Удалить">
                                <i className="fas fa-ellipsis-v text-xs" aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </AdminTableWrap>
            </div>

            {total > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 text-sm text-dc-muted">
                <span>
                  {rangeFrom}–{rangeTo} из {total}
                </span>
                <div className="flex items-center gap-1">
                  <button type="button" className={btnSecondary} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    ‹
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm ${
                        p === page ? 'bg-dc-primary text-white' : 'text-slate-600 hover:bg-slate-100'
                      }`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                  {totalPages > 5 && <span className="px-1">…</span>}
                  <button type="button" className={btnSecondary} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    ›
                  </button>
                </div>
                <span className="text-xs">{pageSize} на странице</span>
              </div>
            )}
          </AdminCard>
        </div>

        <ResizableRecordPanelShell>
          <RecordFormPanel
            key={formMode === 'edit' ? editingRecord?.id : 'create'}
            mode={formMode}
            companyId={companyId}
            contextLabel={contextLabel}
            industryName={context?.industry_name}
            sectorName={context?.sector_name}
            companyName={companyName}
            record={editingRecord}
            variant="inline"
            onSaved={async (_saved, action) => {
              await refreshAll();
              if (action === 'close' && formMode === 'edit') {
                setFormMode('create');
                setEditingRecord(null);
              }
            }}
            onCancel={
              formMode === 'edit'
                ? () => {
                    setFormMode('create');
                    setEditingRecord(null);
                  }
                : undefined
            }
          />
        </ResizableRecordPanelShell>
      </div>

      {previewRecord && (
        <RecordPreviewModal
          record={previewRecord}
          onClose={() => setPreviewRecord(null)}
          onEdit={() => openEdit(previewRecord)}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Удалить запись?"
          message={`Запись «${deleteTarget.company_role_name}» будет удалена без возможности восстановления.`}
          confirmLabel="Удалить"
          danger
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}

      {editCompanyOpen && (
        <AdminModalTw
          title="Редактировать компанию"
          onClose={() => setEditCompanyOpen(false)}
          footer={
            <>
              <button type="button" className={btnSecondary} onClick={() => setEditCompanyOpen(false)}>
                Отмена
              </button>
              <button
                type="button"
                className={btnPrimary}
                disabled={editSaving}
                onClick={async () => {
                  setEditSaving(true);
                  setEditError(null);
                  try {
                    await updateCompany(companyId, { name: editCompanyName.trim() });
                    setEditCompanyOpen(false);
                    await loadCompany();
                  } catch (error) {
                    setEditError(error instanceof Error ? error.message : 'Ошибка сохранения');
                  } finally {
                    setEditSaving(false);
                  }
                }}
              >
                {editSaving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </>
          }
        >
          {editError && <p className="mb-3 text-sm text-red-600">{editError}</p>}
          <AdminFieldTw label="Название компании" required>
            <input className={inputClass} value={editCompanyName} onChange={(e) => setEditCompanyName(e.target.value)} />
          </AdminFieldTw>
        </AdminModalTw>
      )}
    </div>
  );
}
